import type { TilePos } from "@/lib/pixi/isoCoords";

/**
 * 工場シーン (Layout に代わるオブジェクト配置モデル)。
 *
 * 各 ObjectKind ごとに最大 10 個まで配置可能。
 * - エディタが直接編集する真の状態。
 * - シリアライズ可能 (JSON Import/Export 対応)。
 * - 純 TS 層 (React/Pixi/Zustand 非依存)。
 */

export type ObjectKind = "conveyor" | "press" | "arm" | "transport" | "sensor";

/** Scene 内のオブジェクト数の上限 (UI バリデーション用) */
export const MAX_OBJECTS_PER_KIND: Record<ObjectKind, number> = {
  conveyor: 10,
  press: 10,
  arm: 10,
  transport: 10,
  sensor: 10,
};

/**
 * コンベア。直線セグメントの想定だが、内部表現は連続タイル列なので
 * 将来 L 字対応も path 配列拡張だけで可能。
 */
export interface ConveyorObject {
  id: string;
  kind: "conveyor";
  /** 連続する 4-近傍タイル列。先頭がワーク投入起点 (isEntry=true のとき)、末端がバッファ。 */
  path: TilePos[];
  /** ワーク投入を担う start point になるか (P3 の複数コンベア対応で利用) */
  isEntry: boolean;
  /** 終端バッファのスロット数 */
  bufferCapacity: number;
}

export interface PressObject {
  id: string;
  kind: "press";
  /** プレス機のタイル位置。所属コンベアの path 上のタイルを指す必要がある。 */
  position: TilePos;
  /** どのコンベア上に配置されているか (Conveyor.id) */
  conveyorId: string;
}

export interface ArmObject {
  id: string;
  kind: "arm";
  /** アームの基部タイル */
  position: TilePos;
  /** ピック対象のタイル (4-近傍隣接) */
  pickFromTile: TilePos;
  /** プレース対象のタイル (4-近傍隣接) */
  placeToTile: TilePos;
}

export interface TransportObject {
  id: string;
  kind: "transport";
  /** 積み込み位置 (アームの placeTo タイルと同じ想定) */
  parking: TilePos;
  /** 積み下ろし位置 */
  unloading: TilePos;
  /**
   * parking → ... → unloading のウェイポイント列。
   * 端点を含み、隣接ウェイポイント間は直交直線で繋がる前提。
   * 自動経路: routeAutoMode=true のとき auto-routing で算出、
   * 手動編集: ユーザーがウェイポイントを上書き可能 (P4)。
   */
  route: TilePos[];
  /** ウェイポイント自動算出モード (true: 自動 / false: ユーザー手動) */
  routeAutoMode: boolean;
  /** 積載上限 */
  capacity: number;
}

export interface SensorObject {
  id: string;
  kind: "sensor";
  position: TilePos;
}

/** 全オブジェクトの配列を保持する Scene 全体 */
export interface FactoryScene {
  conveyors: ConveyorObject[];
  presses: PressObject[];
  arms: ArmObject[];
  transports: TransportObject[];
  sensors: SensorObject[];
}

export type SceneObject = ConveyorObject | PressObject | ArmObject | TransportObject | SensorObject;
